import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Dumbbell, Image, NotebookTabs, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAdminApi } from '@app/api/api-client-context'
import { useAuth } from '@app/auth/auth-context'
import { unwrapApiResponse } from '@shared/api'
import { interfaceMessages } from '@shared/i18n/interface-messages'
import { ErrorState, LoadingState, PageHeader } from '@shared/ui/page'

const sections = [
	{
		title: 'Упражнения',
		description: 'Каталог, свойства, мышцы, фото, видео и управление статусами.',
		to: '/exercises',
		icon: Dumbbell,
		operation: 'listExercises' as const,
	},
	{
		title: 'Программы',
		description: 'Агрегаты программ, недели, дни и публикация.',
		to: '/programs',
		icon: NotebookTabs,
		operation: 'listPrograms' as const,
	},
	{
		title: 'Медиатека',
		description: 'Загрузка файлов, варианты и состояние обработки.',
		to: '/media',
		icon: Image,
		operation: 'listMedia' as const,
	},
]

export const DashboardPage = () => {
	const api = useAdminApi()
	const { can } = useAuth()
	const query = useQuery({
		queryKey: ['admin', 'dashboard-capabilities'],
		queryFn: async () => {
			const [session, capabilities] = await Promise.all([
				api.GET('/admin/v1/session'),
				api.GET('/admin/v1/capabilities'),
			])
			return {
				session: unwrapApiResponse(session).data,
				capabilities: unwrapApiResponse(capabilities).data,
			}
		},
	})

	return (
		<section className='space-y-6'>
			<PageHeader
				eyebrow={interfaceMessages.sections.dashboard}
				title='Администрирование контента TISIT'
				description='Единая административная поверхность для управления упражнениями, программами и медиаматериалами.'
			/>
			{query.isPending ? <LoadingState label='Проверка сессии и административных возможностей…' /> : null}
			{query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
			{query.data ? (
				<>
					<div className='grid gap-4 md:grid-cols-3'>
						<Card>
							<CardHeader>
								<CardDescription>Текущая сессия</CardDescription>
								<CardTitle className='truncate text-lg'>
									{query.data.session.principal.username ?? query.data.session.principal.subject}
								</CardTitle>
							</CardHeader>
							<CardContent className='text-sm text-muted-foreground'>
								{query.data.session.principal.roles.join(', ')}
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardDescription>BFF contract</CardDescription>
								<CardTitle className='text-lg'>{query.data.capabilities.service}</CardTitle>
							</CardHeader>
							<CardContent className='text-sm text-muted-foreground'>
								Версия {query.data.capabilities.version}
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardDescription>Policy matrix</CardDescription>
								<CardTitle className='flex items-center gap-2 text-lg'>
									<ShieldCheck className='h-5 w-5 text-emerald-600' />
									{query.data.capabilities.operations.length} операций
								</CardTitle>
							</CardHeader>
							<CardContent className='text-sm text-muted-foreground'>
								BFF остаётся источником истины для авторизации.
							</CardContent>
						</Card>
					</div>
					<div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
						{sections.filter(section => can(section.operation)).map(section => {
							const Icon = section.icon
							return (
								<Card key={section.to} className='flex flex-col'>
									<CardHeader>
										<div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary'>
											<Icon className='h-5 w-5' />
										</div>
										<CardTitle className='text-lg'>{section.title}</CardTitle>
										<CardDescription>{section.description}</CardDescription>
									</CardHeader>
									<CardContent className='mt-auto'>
										<Button asChild variant='outline' className='w-full justify-between'>
											<Link to={section.to}>
												Открыть раздел
												<ArrowRight className='h-4 w-4' />
											</Link>
										</Button>
									</CardContent>
								</Card>
							)
						})}
					</div>
				</>
			) : null}
		</section>
	)
}
